import { useMemo, useState } from 'react';
import { KeyRound, Loader2, ShieldCheck, ShieldOff } from 'lucide-react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const TwoFactorSettingsCard = ({ twoFactorState, emailVerified, onStatusChange }) => {
  const [disablePassword, setDisablePassword] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const isEnabled = Boolean(twoFactorState?.two_factor_enabled);
  const methodLabel = useMemo(() => {
    if (twoFactorState?.two_factor_method === 'totp') return 'Application Authenticator';
    if (twoFactorState?.two_factor_method === 'email_otp') return 'Email OTP (compatibilité)';
    return 'TOTP';
  }, [twoFactorState]);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/totp/enroll/start`, {});
      setEnrollment(response.data);
      setConfirmationCode('');
      toast.success('Secret généré. Confirmez le code avant activation.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d activer la verification en deux etapes.");
    } finally {
      setEnabling(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmationCode.trim() || !enrollment?.enrollment_id) return;
    setEnabling(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/totp/enroll/confirm`, {
        enrollment_id: enrollment.enrollment_id,
        code: confirmationCode.trim(),
      });
      onStatusChange?.(response.data?.two_factor);
      setRecoveryCodes(response.data?.recovery_codes || []);
      setEnrollment(null);
      setConfirmationCode('');
      toast.success('MFA activée. Conservez vos codes de récupération hors ligne.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code Authenticator invalide.');
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast.error('Renseignez votre mot de passe actuel pour desactiver la verification en deux etapes.');
      return;
    }

    setDisabling(true);
    try {
      const response = await axios.post(`${API}/account/2fa/disable`, {
        current_password: disablePassword,
      });
      const nextState = response.data?.data?.two_factor || {
        two_factor_enabled: false,
        two_factor_method: null,
      };
      setDisablePassword('');
      onStatusChange?.(nextState);
      toast.success(response.data?.message || 'Verification en deux etapes desactivee.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de desactiver la verification en deux etapes.");
    } finally {
      setDisabling(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-red-600" />
          Verification en deux etapes
        </CardTitle>
        <CardDescription>
          Protégez la connexion et les actions sensibles avec une application Authenticator.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={isEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
              {isEnabled ? 'Activee' : 'Desactivee'}
            </Badge>
            <Badge variant="outline">{methodLabel}</Badge>
          </div>
          <p className="mt-3 text-sm text-slate-700">
            {isEnabled
              ? 'Un second facteur est demandé à la connexion et pour les actions sensibles.'
              : 'Le secret reste en attente tant qu un premier code TOTP n a pas été validé.'}
          </p>
        </div>

        {!emailVerified && !isEnabled && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Verifiez d abord votre email pour pouvoir activer la verification en deux etapes.
          </div>
        )}

        {!isEnabled ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-700">
              <div className="flex items-start gap-3">
                <KeyRound className="mt-0.5 h-4 w-4 text-red-600" />
                <div>
                  <p className="font-medium text-slate-900">Application Authenticator (TOTP)</p>
                  <p className="mt-1">
                    Compatible avec Google Authenticator, Microsoft Authenticator et les gestionnaires de mots de passe TOTP.
                  </p>
                </div>
              </div>
            </div>
            {enrollment && (
              <div className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-slate-700">Saisissez ce secret dans votre application, puis confirmez le code affiché.</p>
                <code className="block break-all rounded bg-white p-2 text-sm" data-testid="totp-secret">{enrollment.secret}</code>
                <Label htmlFor="totp-confirmation-code">Code à 6 chiffres</Label>
                <Input id="totp-confirmation-code" data-testid="totp-confirmation-code" value={confirmationCode} onChange={(event) => setConfirmationCode(event.target.value)} autoComplete="one-time-code" />
                <Button type="button" onClick={handleConfirm} disabled={enabling || !confirmationCode.trim()}>Confirmer et activer</Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                className="bg-red-500 hover:bg-red-600"
                disabled={enabling || !emailVerified}
                onClick={handleEnable}
              >
                {enabling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                {enabling ? 'Préparation...' : 'Configurer Authenticator'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              La verification en deux etapes est activee pour les actions sensibles prises en charge.
            </div>
            <div className="space-y-2">
              <Label htmlFor="two_factor_disable_password">Mot de passe actuel</Label>
              <Input
                id="two_factor_disable_password"
                data-testid="two-factor-disable-password"
                type="password"
                value={disablePassword}
                onChange={(event) => setDisablePassword(event.target.value)}
                placeholder="Confirmez votre mot de passe"
              />
            </div>
            <div className="flex justify-end">
              <Button type="button" variant="outline" disabled={disabling} onClick={handleDisable}>
                {disabling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}
                {disabling ? 'Desactivation...' : 'Desactiver la verification en deux etapes'}
              </Button>
            </div>
          </div>
        )}
        {recoveryCodes.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="recovery-codes">
            <p className="font-semibold text-amber-900">Codes de récupération — affichés une seule fois</p>
            <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">{recoveryCodes.map((code) => <code key={code}>{code}</code>)}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorSettingsCard;
